import React, { useMemo } from 'react';
import { Select } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { changeLanguage, matchLocale, useAvailableLocales, useLocale } from '../../i18n';

interface LanguageSwitcherProps {
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
}

/**
 * Language picker built from the backend manifest (`GET /api/i18n/locales`),
 * falling back to the bundled locale folders. Each option shows its native name.
 */
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ size = 'middle', style }) => {
  const { t } = useTranslation('layout');
  const locale = useLocale();
  const locales = useAvailableLocales();

  const options = useMemo(
    () => locales.map((l) => ({ value: l.code, label: l.nativeName, title: l.name })),
    [locales],
  );
  const value = matchLocale(locale, locales.map((l) => l.code)) ?? locale;

  return (
    <Select
      size={size}
      value={value}
      options={options}
      onChange={(code: string) => {
        void changeLanguage(code);
      }}
      suffixIcon={<GlobalOutlined />}
      popupMatchSelectWidth={false}
      aria-label={t('languageSwitcher.ariaLabel')}
      style={{ minWidth: 120, ...style }}
    />
  );
};

export default LanguageSwitcher;
